import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import styled from "styled-components"
import { orderBy } from "lodash"

import GenericFilter from "Components/Generic/GenericFilter"

// Utils
import { valeursAffichees, VALEUR_VIDE, restreindreValeurs } from "./dropDownValues"

// Réexportée : `containers/Widget` la connaît par ce chemin, et c'est le composant qui la publie.
export { VALEUR_VIDE }

const Container = styled.div`
  border-radius: ${({dropdownBorderRadius, activeBorder}) => (activeBorder && dropdownBorderRadius) && dropdownBorderRadius};
  border-style: ${({dropdownBorderStyle, activeBorder}) => (activeBorder && dropdownBorderStyle) && dropdownBorderStyle};
  border-color: ${({dropdownBorderColor, activeBorder}) => (activeBorder && dropdownBorderColor) && dropdownBorderColor};
  border-width: ${({dropdownBorderSize, activeBorder}) => (activeBorder && dropdownBorderSize) && dropdownBorderSize};
  height: 50px;
`

function DropDownList({
  defaultColor,
  activeBorder,
  dropdownBorderRadius,
  dropdownBorderColor,
  dropdownBorderSize,
  dropdownBorderStyle,
  data,
  placeholder,
  value,
  sideMenuColor,
  onSelectFilterValue,
  sortingType,
  filterType,
  timingBeforeSelectActivation,
  widgetTraduction,
  /**
   * Ajoute une option « Vide » qui sélectionne les fiches dont la colonne n'est pas renseignée.
   *
   * ⚠️ Défaut `false`, et c'est le point important : ce composant sert TOUS les filtres du produit.
   * Les valeurs vides sont retirées des options depuis toujours ; les rendre sélectionnables
   * partout changerait le comportement de dashboards que personne ne nous a demandé de modifier.
   * Le réglage s'active filtre par filtre — par le template, ou dans l'écran de configuration.
   */
  allowEmptyOption = false,
  /**
   * Valeurs à EXCLURE de la liste, ou seules valeurs À PROPOSER : `{ mode, values }`.
   * Ne restreint que la liste proposée, jamais les données de la page — cf. `restreindreValeurs`.
   */
  valueFilter,
}) {
  const [changingTimeout, setChangingTimeout] = useState(0)
  const [listValues, setListValue] = useState([])
  const [listOptions, setListOptions] = useState([])

  useEffect(() => {
    const brutes = data?.unique ?? []
    const estVide = v => v === null || v === undefined || v === ""
    let options = restreindreValeurs([...brutes].filter(v => !estVide(v)), valueFilter)
    // L'option n'est proposée QUE si la colonne porte réellement des valeurs vides : un filtre où
    // toutes les fiches sont renseignées offrirait sinon un choix qui ne ramène jamais rien.
    const proposerVide = allowEmptyOption && brutes.some(estVide)
    const boolLabel = (v) => v ? (widgetTraduction?.BOOL_TRUE || "Yes") : (widgetTraduction?.BOOL_FALSE || "No")
    // Une seule source pour le libellé : l'option proposée et la pastille affichée doivent dire le
    // même mot, sans quoi on retombe sur le défaut corrigé ici.
    const libelleVide = widgetTraduction?.EMPTY_VALUE || "Empty"
    if(!options.length && !proposerVide) setListOptions([])
    else {
      if(typeof options[0] === "number"){
        if(sortingType === "ascending"){
          options.sort((a,b) => a - b)
        } else if(sortingType === "descending"){
          options.sort((a,b) => b - a)
        }
      } else if (typeof options[0] === "string") {
        if(sortingType === "ascending"){
          options.sort((a,b) => a.localeCompare(b))
        } else if(sortingType === "descending"){
          options.sort((a,b) => b.localeCompare(a))
        }
      } else if (typeof options[0] === "boolean") {
        if(sortingType === "ascending"){
          options.sort((a, b) => (a === b ? 0 : a ? -1 : 1))
        } else if(sortingType === "descending"){
          options.sort((a, b) => (a === b ? 0 : a ? 1 : -1))
        }
      }
      // Libellé combiné (`widgetProps.optionLabel`, cf. `Utils/optionLabel`) : la VALEUR reste celle
      // du champ filtré, seul le texte affiché change.
      const libelles = data?.labels ?? null
      let optionsToSend = options.map(optionValue => ({
        value: optionValue,
        label: libelles?.[String(optionValue)] ??
          (typeof optionValue === "boolean" ? boolLabel(optionValue) : optionValue)
      }))
      // Avec un libellé combiné, le tri porte sur ce que l'utilisateur LIT (« M0001 - … » par code),
      // et non sur la valeur cachée derrière — décision du 01/10/2026.
      if (libelles && (sortingType === "ascending" || sortingType === "descending")) {
        const sens = sortingType === "ascending" ? 1 : -1
        optionsToSend.sort((a, b) => sens * String(a.label).localeCompare(String(b.label)))
      }
      // ⚠️ En DERNIER, jamais trié avec les autres : « Vide » n'est pas une valeur de la colonne,
      // c'est l'absence de valeur. La placer dans l'ordre alphabétique la ferait passer pour une.
      if (proposerVide) {
        optionsToSend = [
          ...optionsToSend,
          { value: VALEUR_VIDE, label: libelleVide },
        ]
      }
      setListOptions(optionsToSend)
    }
    if(!Array.isArray(value) || value === "default") setListValue([])
    else {
      setListValue(valeursAffichees(value, boolLabel, libelleVide, data?.labels ?? null))
    }
    // ⚠️ Le libellé de « Vide » est une DONNÉE de cet effet : sans lui dans les dépendances, une
    // traduction arrivée après le premier rendu laisserait « Empty » à l'écran. On dépend de la
    // chaîne, pas de l'objet, dont l'identité change à chaque rendu du parent.
    // `valueFilter` par sa forme sérialisée : l'objet est recréé à chaque rendu du parent.
  },[data, sortingType, value, widgetTraduction?.EMPTY_VALUE, JSON.stringify(valueFilter ?? null)])

  // const onFilterChange = (eltSelected) => {
  //   onSelectFilterValue(eltSelected)
  // }

  const onFilterChange = (eltSelected) => {
    if(timingBeforeSelectActivation === 0) {
      onSelectFilterValue(eltSelected)
    } else {
      if (changingTimeout) {
        clearTimeout(changingTimeout)
      }
  
      setChangingTimeout(setTimeout(function () {
        onSelectFilterValue(eltSelected)
      }, timingBeforeSelectActivation))
    }
  }
  
  return (
    <Container
      id="DropDownListContainer"
      activeBorder={activeBorder}
      dropdownBorderRadius={dropdownBorderRadius}
      dropdownBorderColor={dropdownBorderColor}
      dropdownBorderSize={dropdownBorderSize}
      dropdownBorderStyle={dropdownBorderStyle}
    >
      <GenericFilter
        options={listOptions}
        placeholder={placeholder}
        scrollBarColor={sideMenuColor}
        useSelectAllOption={true}
        isMulti={filterType === "multiSelect"}
        inputBackgroundColor={defaultColor}
        borderRadius={dropdownBorderRadius}
        onChange={(obj) => onFilterChange(obj)}
        partialTrad={widgetTraduction}
        {...(listValues && listValues.length) &&
          {
            value: listValues.length > 1 ? listValues : listValues[0]
          }
        }
      />
    </Container>
  )
}

DropDownList.propTypes = {
  defaultColor: PropTypes.string,
  borderRadius: PropTypes.string,
  data: PropTypes.object,
  placeholder: PropTypes.string,
  filterType: PropTypes.string,
  timingBeforeSelectActivation: PropTypes.number,
  /** Ajoute une option « Vide » — opt-in par filtre, cf. le bloc du composant. */
  allowEmptyOption: PropTypes.bool,
  /** Exclure ou n'accepter que certaines valeurs — opt-in par filtre, cf. le bloc du composant. */
  valueFilter: PropTypes.shape({
    mode: PropTypes.oneOf(["exclude", "include"]),
    values: PropTypes.array,
  }),
  /** Dictionnaire des libellés du widget. Déjà lu avant ce lot, mais jamais déclaré. */
  widgetTraduction: PropTypes.object,
}

DropDownList.defaultProps = {
  defaultColor: "#5588FF",
  borderRadius: '5px',
  data: {
    "unique": [],
    "#unique": 0
  },
  value: "default",
  name: "defaultName",
  sortingType: "default",
  color: "",
  style: {},
  placeholder: "Select",
  filterType: "singleSelect",
  onSelectFilterValue: (obj) => console.log(obj),
  timingBeforeSelectActivation: 0,
}


export default DropDownList
